import type { App, CustomerFacingApp } from "@teamworksdev/react";
import type { Maybe, MaybeArray } from "~/utils/types";
import type { ConstrainedDocumentScope, DocumentDescriptor, DocumentStatus, DocumentType } from "../documents";

type Gender =
  | "Woman"
  | "Man"
  | "Transgender Woman"
  | "Transgender Man"
  | "Non-binary"
  | "Agender"
  | "Gender-fluid"
  | "Gender not listed"
  | "Prefer not to say"
  | "Other";

export type Person = {
  _original: object;
  id: string;
  apps: App[];
  active: boolean;
  inactiveApps: App[];
  appStatus: Record<App, DocumentStatus>;
  name: string;
  givenName: Maybe<string>;
  preferredName: Maybe<string>;
  familyName: Maybe<string>;
  email: Maybe<string>;
  primaryPhone: Maybe<string>;
  birthDate: Maybe<string>;
  clientUniqueId: Maybe<string>;
  hubProfileId: Maybe<string>;
  activeWithoutLoginAccess: Maybe<boolean>;
  inflcrProfileId: Maybe<string>;
  imageUrl: Maybe<string>;
  webAccess: Partial<Record<App, boolean>>;
  mobileAccess: Partial<Record<App, boolean>>;
  oktaUserId: Maybe<string>;
};

export const MatchUpsertReviewAction = {
  merge: "merge",
  create: "create_new",
  delete: "delete",
} as const;
export type MatchUpsertReviewAction = (typeof MatchUpsertReviewAction)[keyof typeof MatchUpsertReviewAction];
export type ReviewEntry = {
  partial_match_id: number;
  merge_into_document_id?: Maybe<string>;
  action: MatchUpsertReviewAction;
};

export type ProfileDocument = DocumentDescriptor<PartialMatchScopeData, typeof DocumentType.profile>;

type TeamPosition = {
  teamPositionName: string;
  teamPositionType: string;
};

export type Membership = {
  teamId: string;
  sportName: string;
  jerseyNumber: Maybe<string>;
  userTypeIds: string[];
  athleteStatus: string;
  athleteYear: Maybe<string>;
  positions: string[];
  teamPositions: Maybe<TeamPosition[]>;
};

export type ScopeData = {
  birthdate: Maybe<string>;
  email: Maybe<string>;
  familyName: Maybe<string>;
  givenName: Maybe<string>;
  name: Maybe<string>;
  preferredName: Maybe<string>;
  primaryPhone: Maybe<string>;
  profileId?: Maybe<string>;
  clientUniqueId: Maybe<string>;
  memberships: Maybe<Membership[]>;
  status: Maybe<DocumentStatus>;
};

export type PartialMatchScopeData = ScopeData & {
  /** Derived properties */
  app?: App;
  scopes?: Partial<Record<App, ScopeData>>;
};

export type PartialMatch = {
  _original: object;
  /** Raw properties */
  app: App;
  birthdate: Maybe<string>;
  email: Maybe<string>;
  familyName: Maybe<string>;
  givenName: Maybe<string>;
  matchDate: string;
  orgId: string;
  phone: Maybe<string>;
  preferredName: Maybe<string>;
  uniqueId: string;
  partialMatchId: string;
  data: {
    orgId: string;
    scopes: Partial<Record<string, ScopeData>>;
    status: DocumentStatus;
    invalidation_action: Maybe<string>;
  };
  /** Derived properties */
  name: string;
};

export type MatchDocument = {
  apps: App[];
  documentId: Maybe<string>;
  clientUniqueId?: Maybe<string>;
  scopes: Partial<Record<App, ScopeData>>;
};

export type PartialMatchDetail = PartialMatch & {
  matchedDocuments: Maybe<MatchDocument[]>;
  scopes: Partial<Record<App, ScopeData>>;
};

export type PartialMatchSearchParams = {
  offset?: number;
  limit?: number;
  search?: string;
  order_by?: string;
  apps?: App[];
  by_org_ids?: string[];
  hub_athlete_status_ids?: string[];
  active_only?: boolean;
};

export type Profile = {
  givenName: string;
  familyName: string;
  email: string;
  preferredName: Maybe<string>;
  birthdate: Maybe<string>;
  primaryPhone: Maybe<string>;
  clientUniqueId: Maybe<string>;
  imageUrl: Maybe<string>;
  gender: Maybe<Gender>;
  otherNames: MaybeArray<string>;
  otherPhones: MaybeArray<string>;
  otherEmails: MaybeArray<string>;
  preferredPronouns: MaybeArray<string>;
  profileId: Maybe<string>;
  activeWithoutLoginAccess: Maybe<boolean>;
};

export type AppProfile = Profile & {
  profileId: Maybe<string>;
  hasWebAccess: boolean;
  hasMobileAccess: boolean;
} & {};

export type AxleProfile = Profile & {
  uniqueClientId: Maybe<string>;
};

export type ArmsProfile = AppProfile & {
  lastLogin: Maybe<string>;
};

export type SupportedProfile = AppProfile & AxleProfile & ArmsProfile;

export type ProfileDocumentData<T extends App, K extends Profile = Profile> = {
  app: T;
  status: DocumentStatus;
  data: ConstrainedDocumentScope<K, T, typeof DocumentType.profile>;
};

export type ProfileData<T extends App = App, K extends Profile = Profile> = Record<T, ProfileDocumentData<T, K>>;

type AxleProfileData = ProfileData<App.axle, AxleProfile>;
type ArmsProfileData = ProfileData<App.arms, ArmsProfile>;

export type SupportedProfileData = ProfileData & AxleProfileData & ArmsProfileData;

export type LoginAccess = {
  web: Partial<Record<CustomerFacingApp, boolean>>;
  mobile: Partial<Record<CustomerFacingApp, boolean>>;
};

export type RawProfileDocument = {
  created_by_app: string;
  created_date: string;
  data: SupportedProfileData;
  document_id: string;
  json_schema: unknown;
  login_access: null | LoginAccess;
  org_id: string;
  status: DocumentStatus;
};

export type CreateUMAImpersonationGrantRequest = { impersonated_person_document_id: string };

export type DuplicateProfileGroup = {
  documentIds: string[];
  displayName: string;
};

export type DuplicateProfilesPage = {
  perPage: number;
  nextPage: Maybe<string>;
  currentPage: Maybe<string>;
  previousPage: Maybe<string>;
  totalGroups: number;
  items: DuplicateProfileGroup[];
};

// Mirror of DuplicateProfileMatchField in axle/schemas/profiles.py — keep in sync.
export const DuplicateProfileMatchField = {
  familyName: "familyName",
  givenName: "givenName",
  email: "email",
  primaryPhone: "primaryPhone",
  clientUniqueId: "clientUniqueId",
} as const;
export type DuplicateProfileMatchField = (typeof DuplicateProfileMatchField)[keyof typeof DuplicateProfileMatchField];

export type FindDuplicateProfilesParams = {
  perPage?: number;
  cursor?: Maybe<string>;
  matchFields: DuplicateProfileMatchField[];
  app?: string;
  search?: string;
};
